import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";

import { LineChart } from "@design-system/LineChart/LineChart";

import { useEmployeeApplicationUsageLineChartQuery } from "./ClientEmployeeAppUsageLineChart.g";

type Props = {
  employeeId: string;
  daysToShow?: number;
  startDate?: Date;
  endDate?: Date;
  parentCategoryId?: string;
  applicationResourceId?: string;
};

const formatQueryDate = (date: Date | Dayjs): string =>
  dayjs(date).format("YYYY-MM-DD");

const resolveQueryBounds = (
  startDate: Date | undefined,
  endDate: Date | undefined,
  daysToShow: number,
): { dateFrom?: string; dateTo: string; openStart: boolean } => {
  if (startDate && endDate) {
    return {
      dateFrom: formatQueryDate(startDate),
      dateTo: formatQueryDate(endDate),
      openStart: false,
    };
  }
  if (startDate) {
    return {
      dateFrom: formatQueryDate(startDate),
      dateTo: formatQueryDate(dayjs()),
      openStart: false,
    };
  }
  if (endDate) {
    return {
      dateTo: formatQueryDate(endDate),
      openStart: true,
    };
  }
  const end = dayjs();
  return {
    dateFrom: formatQueryDate(end.subtract(daysToShow, "day")),
    dateTo: formatQueryDate(end),
    openStart: false,
  };
};

const buildAxisDays = (start: Dayjs, end: Dayjs): Dayjs[] => {
  const dayCount = Math.max(1, end.diff(start, "day") + 1);
  return Array.from({ length: dayCount }, (_, i) =>
    start.add(i, "day"),
  );
};

export const ClientEmployeeAppUsageLineChart = ({
  employeeId,
  daysToShow = 30,
  startDate,
  endDate,
  parentCategoryId,
  applicationResourceId,
}: Props) => {
  const queryBounds = useMemo(
    () => resolveQueryBounds(startDate, endDate, daysToShow),
    [startDate, endDate, daysToShow],
  );

  const applicationEmployeeUsageLineChartQuery =
    useEmployeeApplicationUsageLineChartQuery({
      employeeId,
      dateFrom: queryBounds.dateFrom,
      dateTo: queryBounds.dateTo,
      ...(parentCategoryId && { categoryIds: [parentCategoryId] }),
      ...(applicationResourceId && { applicationResourceId }),
    });

  const applicationUsageByDate = useMemo(() => {
    const dailySummary =
      applicationEmployeeUsageLineChartQuery.data
        ?.clientEmployeeApplicationInteractions?.dailySummary;

    if (!dailySummary || dailySummary.length === 0) {
      return [];
    }

    return dailySummary.map((summary) => ({
      date: dayjs(summary.date).format("DD/MM/YYYY"),
      isoDate: dayjs(summary.date).format("YYYY-MM-DD"),
      resourceGroupCount: summary.uniqueResourceGroups,
    }));
  }, [applicationEmployeeUsageLineChartQuery.data]);

  const data = useMemo(() => {
    const resourceGroupCountsByDate = new Map<string, number>();
    applicationUsageByDate.forEach((usage) => {
      if (usage.date && usage.resourceGroupCount !== undefined) {
        resourceGroupCountsByDate.set(usage.date, usage.resourceGroupCount);
      }
    });

    let axisStart: Dayjs | null;
    const axisEnd = dayjs(queryBounds.dateTo);

    if (queryBounds.openStart) {
      if (applicationUsageByDate.length === 0) {
        return { chartData: [], weekendDates: [] };
      }
      axisStart = applicationUsageByDate.reduce((earliest, usage) => {
        const current = dayjs(usage.isoDate);
        return current.isBefore(earliest) ? current : earliest;
      }, dayjs(applicationUsageByDate[0].isoDate));
    } else {
      axisStart = dayjs(queryBounds.dateFrom);
    }

    const days = buildAxisDays(axisStart, axisEnd);
    const chartData = days.map((currentDate) => {
      const dateStr = currentDate.format("DD/MM/YYYY");
      return {
        x: dateStr,
        y: resourceGroupCountsByDate.get(dateStr) || 0,
      };
    });

    const weekendDates = days
      .filter((currentDate) => currentDate.day() === 0 || currentDate.day() === 6)
      .map((currentDate) => currentDate.format("DD/MM/YYYY"));

    return { chartData, weekendDates };
  }, [applicationUsageByDate, queryBounds]);

  const chartKey = useMemo(() => {
    return `${queryBounds.dateFrom ?? "open"}-${queryBounds.dateTo}-${parentCategoryId || "all"}-${applicationResourceId || "all"}`;
  }, [queryBounds, parentCategoryId, applicationResourceId]);

  return (
    <LineChart
      key={chartKey}
      data={
        data.chartData.length === 0
          ? []
          : [
              {
                id: "resource-group-usage",
                color: "red",
                data: data.chartData,
              },
            ]
      }
      weekendDates={data.weekendDates}
      xAxisName=""
      yAxisName="Resource Groups"
      tooltipLabel="Resource Groups"
    />
  );
};
